import { StyleSheet } from "react-native";

import type { UiTheme } from "../theme";

// PE-22 F — the weeks x days matrix that replaced the composer's staged D/W
// chip strips and the spine's day picker. Metrics deliberately mirror
// IntensityGrid/gridStyles.ts (44pt rows, 28px axis gutter, invisible ring on
// the baseline row so selecting never shifts layout) so the two grids read as
// one family — the derived intensity readout of fetta G sits right under this.
export function makePlacementMatrixStyles(theme: UiTheme) {
  const p = theme.palette;
  const s = theme.spacing;
  const r = theme.radius;
  const t = theme.typography;

  return StyleSheet.create({
    wrap: { gap: s.s2 },
    headRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: s.s1,
      marginBottom: s.s1,
    },
    // The axis gutter: week labels on the left, empty above them.
    gutter: { width: 28 },
    dayHead: {
      flex: 1,
      textAlign: "center",
      color: p.ink3,
      fontSize: t.sizes.xs,
      fontVariant: ["tabular-nums"],
    },
    // brandText, non brand500: sono NUMERI, cioe' testo, e brand500 su surface
    // sta a 3,12:1 — sotto il 4,5 di AA. E' il difetto che brandText esiste per
    // chiudere (DS-11/DS-20), qui re-introdotto e misurato.
    dayHeadSelected: { color: p.brandText, fontWeight: "700" },
    // ⛔ Nessuna riserva di bordo qui: gridStyles la tiene perche' seleziona per
    // RIGA, questa matrice seleziona per CELLA e la riga non dipinge mai un
    // anello. Copiarla sarebbe stato stile morto ereditato da un modello di
    // selezione diverso.
    row: {
      flexDirection: "row",
      alignItems: "center",
      gap: s.s1,
      minHeight: 44,
      borderRadius: r.r3,
      paddingHorizontal: s.s2,
    },
    weekNum: {
      width: 28,
      color: p.ink2,
      fontSize: t.sizes.sm,
      fontWeight: "700",
      fontVariant: ["tabular-nums"],
    },
    weekNumSelected: { color: p.brandText },
    cells: { flex: 1, flexDirection: "row", gap: s.s1 },
    // ⛔ NO minWidth floor here, and the visual bench is why: seven days at
    // 44 each (308) plus the week gutter and the − column is 364 on a 360dp
    // phone, so the worst case OVERFLOWED and the cells rode over the −. The
    // 44pt target is met on the HEIGHT instead and the column takes what the
    // width allows (~40dp at seven days, ~70 at four).
    cell: {
      flex: 1,
      minHeight: 44,
      borderRadius: r.r2,
      borderWidth: 1,
      // borderStrong, non border: border su surface e' 1,29:1, cioe' invisibile,
      // e la maggior parte di una griglia da 84 celle e' a riposo. Stessa cura
      // che DS-9/DS-23 hanno gia' applicato a IconButton (3,67:1).
      borderColor: p.borderStrong,
      backgroundColor: "transparent",
      alignItems: "center",
      justifyContent: "center",
    },
    // Placed: tint + glyph, and a QUIET border. DESIGN.md:166 — outline-off /
    // tint-on, never a filled brand circle, never colour alone (the ✓ carries
    // it too).
    // ⛔ It must NOT borrow the brand border: the bench showed that a placed
    // cell and the cursor then differed by one pixel of border width, which on
    // a phone reads as a rendering artefact — the exact defect PE-22 D2 paid
    // for. The ring belongs to the cursor alone.
    cellPlaced: {
      backgroundColor: p.brand100,
    },
    // The cursor, and nothing else uses it: 2px brand ring. Four states stay
    // legible — empty, placed (tint+✓), cursor-on-empty (ring), cursor-on-
    // placed (ring + tint + ✓).
    cellSelected: {
      borderWidth: 2,
      borderColor: p.brand500,
    },
    cellPressed: { opacity: 0.6 },
    cellGlyph: {
      // brandText: il ✓ su brand100 sta a 2,83:1 con brand500, 4,65:1 con questo.
      color: p.brandText,
      fontSize: t.sizes.sm,
      fontWeight: "700",
    },
    // Navigation-only mode (no exercise open): the cell still shows WHERE you
    // are, but carries no on/off meaning, so it renders a quiet dot instead of
    // an empty box that would read as "not placed".
    cellDot: { color: p.ink3, fontSize: t.sizes.xs },
    // The − that drops this week. Sits at the row's end, past the cells, so it
    // never competes with a cell for the same touch, and clears 44pt because it
    // is destructive.
    // ⛔ It does NOT reserve room for a sentence. The first try parked a 92px
    // column on every row to hold the confirm beside it — permanent width for a
    // rare state, and the sentence wrapped four lines anyway. The house idiom
    // is ConfirmRemoveButton's: SWAP THE LABEL. At rest it is a − in 28px; armed
    // it grows to "✕ Confirm?" and the cells give the space back afterwards.
    // The whole sentence still reaches a screen reader, via accessibilityLabel.
    rowRemove: {
      minWidth: 28,
      minHeight: 44,
      paddingHorizontal: s.s2,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: r.r2,
    },
    // ink2, not ink3: it is destructive, and at ink3 the bench showed it
    // reading as decoration rather than a control.
    rowRemoveGlyph: { color: p.ink2, fontSize: t.sizes.md, fontWeight: "700" },
    // Il colore da solo non basta su un controllo distruttivo: il glifo passa
    // da − a ✕, cosi' lo stato armato si vede anche senza distinguere i toni.
    rowRemoveArmed: { backgroundColor: p.dangerFill },
    rowRemoveGlyphArmed: { color: p.danger },
    addRow: { flexDirection: "row", alignItems: "center", gap: s.s1 },
    addSpacer: { width: 28 },
  });
}
